import SectionLabel from "@/components/SectionLabel";
import EditorialBanner from "@/components/EditorialBanner";
import {
  Building2,
  Landmark,
  Globe2,
  Users2,
} from "lucide-react";

const CASES = [
  {
    icon: Building2,
    tag: "Use case 01",
    audience: "Universal banks",
    title: "Multi-system universal banks",
    note: "Endemic at scale",
    body: "Retail, corporate, private and markets carry separate CIFs. The same person sits in three or four of them. Sentinel links the records without forcing a CIF migration.",
    callout: "1 person · 3–4 CIFs · 1 entity",
  },
  {
    icon: Landmark,
    tag: "Use case 02",
    audience: "Banks in M&A",
    title: "Pre-, mid- and post-merger integration",
    note: "Phase-2/3 data unification, for years",
    body: "Full CIF unification routinely takes 3–7 years. Sentinel surfaces concentration risk, KYC duplication and sanction-screening redundancy from day one — and keeps surfacing it through the long tail.",
    callout: "95% unified ≠ 100% unified",
  },
  {
    icon: Globe2,
    tag: "Use case 03",
    audience: "Cross-jurisdictional groups",
    title: "Subsidiaries the law won't let you merge",
    note: "Regulatory necessity",
    body: "Separate licenses, separate regulators, data localisation. Group-level AML, sanctions and UBO oversight needs the link — Sentinel resolves it without crossing the data wall.",
    callout: "Group oversight, jurisdictionally clean",
  },
  {
    icon: Users2,
    tag: "Use case 04",
    audience: "Every bank with corporates",
    title: "UBO & director networks",
    note: "Universal, underleveraged",
    body: "One person can be UBO or director of five corporates and a retail customer. Concentration risk only appears once the link is drawn — and it never sits in a single CIF row.",
    callout: "1 person · 6 records · risk visible",
  },
  {
    icon: Globe2,
    tag: "Use case 05",
    audience: "BaaS operators",
    title: "Same person across BaaS tenants",
    note: "Original use case",
    body: "Sentinel resolves identity at the group compliance layer. Each tenant sees only its own customers. The operator sees the linkage.",
    callout: "1 person · n tenants · 1 entity",
  },
];

const UseCases = () => {
  return (
    <section
      data-testid="usecases-section"
      id="use-cases"
      className="dark-section relative overflow-hidden py-24 lg:py-32 border-t border-white/10"
    >
      <div className="absolute inset-0 bg-dot-grid opacity-40" />

      <div className="relative max-w-[1380px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-16">
          <div className="lg:col-span-7">
            <SectionLabel index="04" label="Where banks miss risk today" dark />
            <h2 className="mt-6 font-display font-bold text-white text-4xl sm:text-5xl lg:text-[64px] tracking-tight leading-[1.02]">
              Fragmentation is everywhere.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-white/65 text-[17px] leading-relaxed">
              The cleanest narrative for Sentinel is Banking-as-a-Service.
              The largest market is everywhere else.
            </p>
          </div>
        </div>

        <EditorialBanner
          gradient="emerald-cyan"
          image="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=2400&q=80"
          kicker="Missed risk"
          headline="Wherever a real person is split across more than one record &mdash; the risk goes unseen."
          chips={[
            { c: "#7c3aed", t: "Universal banks" },
            { c: "#f59e0b", t: "M&A" },
            { c: "#10b981", t: "Cross-juris" },
          ]}
          testId="usecases-banner"
        />

        {/* Use case grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {CASES.map((c, i) => {
            const Icon = c.icon;
            const isOriginal = i === CASES.length - 1;
            const ACCENTS = [
              { hex: "#7c3aed", soft: "rgba(124,58,237,0.14)" },  // violet — universal banks
              { hex: "#f59e0b", soft: "rgba(245,158,11,0.14)" },  // gold — M&A
              { hex: "#10b981", soft: "rgba(16,185,129,0.14)" },  // emerald — cross-jurisdiction
              { hex: "#f43f5e", soft: "rgba(244,63,94,0.14)" },   // rose — UBO/director
              { hex: "#00e5ff", soft: "rgba(0,229,255,0.14)" },   // cyan — BaaS
            ];
            const a = ACCENTS[i % ACCENTS.length];
            return (
              <div
                key={c.title}
                data-testid={`usecase-${i}`}
                className={`p-7 lg:p-8 flex flex-col gap-5 min-h-[340px] relative ${
                  isOriginal ? "bg-[#13141c]" : "bg-[#0d0e16]"
                }`}
              >
                <span
                  className="absolute top-0 left-0 right-0 h-[3px]"
                  style={{ backgroundColor: a.hex }}
                />
                <div className="flex items-center justify-between">
                  <span
                    className="font-mono text-[10px] tracking-[0.22em] uppercase"
                    style={{ color: a.hex }}
                  >
                    {c.tag}
                  </span>
                  <span
                    className="inline-flex items-center justify-center w-9 h-9"
                    style={{ backgroundColor: a.soft }}
                  >
                    <Icon
                      className="w-4 h-4"
                      style={{ color: a.hex }}
                      strokeWidth={2}
                    />
                  </span>
                </div>

                <div>
                  <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-white/45">
                    {c.audience}
                  </span>
                  <p className="mt-2 font-display font-bold text-white text-[22px] tracking-tight leading-tight">
                    {c.title}
                  </p>
                  <p className="mt-2 font-mono text-[10px] tracking-[0.18em] uppercase text-white/45">
                    {c.note}
                  </p>
                </div>

                <p className="text-white/65 text-[13.5px] leading-relaxed">
                  {c.body}
                </p>

                <div className="mt-auto pt-4 border-t border-white/10">
                  <p
                    className="font-mono text-[11px] tracking-[0.08em]"
                    style={{ color: a.hex }}
                  >
                    {c.callout}
                  </p>
                </div>
              </div>
            );
          })}

          {/* Closing tile */}
          <div
            className="bg-[#0a0b13] p-7 lg:p-8 flex flex-col justify-between min-h-[340px]"
            data-testid="usecase-closing"
          >
            <div>
              <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/45">
                The pattern
              </span>
              <p className="mt-4 font-display font-bold text-white text-[22px] lg:text-[24px] tracking-tight leading-tight">
                Wherever a real person is split across more than one record — Sentinel resolves them.
              </p>
            </div>
            <p className="text-white/55 text-[13px] mt-6">
              No CIF migration. No data movement.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default UseCases;
